import React, { useMemo } from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import styled from "styled-components";

import { color } from "../../tokens";
import { Backdrop } from "../Backdrop/Backdrop";
import { IconButton } from "../IconButton/IconButton";
import { Spinner } from "../Spinner/Spinner";
import { Typography } from "../Typography/Typography";
import { noop } from "../utils";

type SlideDirection = "up" | "down" | "left" | "right" | "none";

export interface Props {
  actions?: React.ReactNode;
  backgroundColor?: string;
  content?: React.ReactNode;
  direction?: SlideDirection;
  isBusy?: boolean;
  isOpen: boolean;
  noBorder?: boolean;
  scroll?: "body" | "paper";
  size?: "sm" | "md" | "lg" | false; // XL removed per design decision
  title?: string | React.ReactNode;
  disableEscapeAndClickOutside?: boolean;
  onDismiss?: () => void;
  hideClose?: boolean;
}

const createTransition = (direction: SlideDirection) =>
  React.forwardRef<unknown, TransitionProps>(function Transition(props, ref) {
    return <Slide direction={direction} ref={ref} {...(props as any)} />;
  });

export const Modal: React.FC<Props> = (props) => {
  const {
    actions,
    backgroundColor = color.neutral[100],
    content,
    direction = "none",
    isBusy,
    isOpen,
    scroll = "paper",
    size = "sm",
    title,
    disableEscapeAndClickOutside = false,
    noBorder = false,
    onDismiss,
    hideClose = false,
  } = props;

  const TransitionComponent = useMemo(
    () => createTransition(direction),
    [direction]
  );

  const handleDismiss = (event: object, reason: string) => {
    if (disableEscapeAndClickOutside && reason === "backdropClick") {
      return;
    }
    onDismiss?.();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isBusy ? noop : handleDismiss}
      slots={{
        transition: direction === "none" ? undefined : TransitionComponent,
      }}
      scroll={scroll}
      maxWidth={size}
      fullWidth={Boolean(size)}
      disableEscapeKeyDown={disableEscapeAndClickOutside}
      sx={{
        "& .MuiDialog-paper": {
          background: backgroundColor,
        },
      }}
    >
      {isOpen && isBusy && (
        <Backdrop isActive={isBusy} opacity={0.35}>
          <SpinnerLayout>
            <Spinner color={color.neutral[100]} size={60} />
          </SpinnerLayout>
        </Backdrop>
      )}

      <TitleRow>
        {title && (
          <>
            {typeof title === "string" ? (
              <Typography variant="titleMedium" component="h1">
                {title}
              </Typography>
            ) : (
              title
            )}
          </>
        )}

        {onDismiss && !disableEscapeAndClickOutside && !hideClose && (
          <IconButton
            aria-label="close"
            color="secondary"
            iconProps={{ icon: "x", size: "xs" }}
            onClick={onDismiss}
          />
        )}
      </TitleRow>

      {!noBorder && <Divider />}

      {content && <DialogContent>{content}</DialogContent>}

      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
};

const TitleRow = styled.div<{ $noBorder?: boolean }>`
  display: flex;
  flexdirection: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding-bottom: 1rem;
`;

const SpinnerLayout = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;
